import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../../theme/Colors';
import Button from '../common/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  resetOnPropsChange?: boolean;
  isolate?: boolean;
  level?: 'page' | 'section' | 'component';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
    
    // Update state with error details
    this.setState(prevState => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
    
    // Here you could also send error to error reporting service
    // Example: Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;
    
    if (hasError && prevProps.resetKeys !== resetKeys) {
      if (resetOnPropsChange) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback, isolate, level = 'component' } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return <>{fallback}</>;
      }

      // Default error UI based on level
      switch (level) {
        case 'page':
          return (
            <PageErrorContainer>
              <ErrorContent>
                <ErrorIcon>⚠️</ErrorIcon>
                <ErrorTitle>Oops! Something went wrong</ErrorTitle>
                <ErrorMessage>
                  We're sorry, but something unexpected happened. Please try refreshing the page.
                </ErrorMessage>
                <ErrorActions>
                  <Button onClick={() => window.location.reload()}>
                    Refresh Page
                  </Button>
                  <Button variant="outline" onClick={this.resetErrorBoundary}>
                    Try Again
                  </Button>
                </ErrorActions>
                {process.env.NODE_ENV === 'development' && (
                  <ErrorDetails>
                    <DetailsTitle>Error Details (Development Only)</DetailsTitle>
                    <ErrorStack>{error.toString()}</ErrorStack>
                    {errorInfo && (
                      <ComponentStack>{errorInfo.componentStack}</ComponentStack>
                    )}
                  </ErrorDetails>
                )}
              </ErrorContent>
            </PageErrorContainer>
          );
          
        case 'section':
          return (
            <SectionErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <ErrorText>This section couldn't load properly</ErrorText>
              <RetryButton onClick={this.resetErrorBoundary}>Retry</RetryButton>
            </SectionErrorContainer>
          );
          
        case 'component':
        default:
          if (isolate) {
            return (
              <ComponentErrorContainer>
                <SmallErrorIcon>!</SmallErrorIcon>
                <SmallErrorText>Unable to load</SmallErrorText>
              </ComponentErrorContainer>
            );
          }
          // For non-isolated component errors, show nothing to prevent breaking the UI
          return null;
      }
    }

    return children;
  }
}

// Styled Components
const PageErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f9fafb;
  padding: 20px;
`;

const ErrorContent = styled.div`
  text-align: center;
  max-width: 500px;
  padding: 40px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const ErrorTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
`;

const ErrorDetails = styled.div`
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid #e5e7eb;
  text-align: left;
`;

const DetailsTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 12px;
`;

const ErrorStack = styled.pre`
  font-size: 12px;
  color: #ef4444;
  background-color: #fee2e2;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin-bottom: 12px;
`;

const ComponentStack = styled.pre`
  font-size: 12px;
  color: #6b7280;
  background-color: #f3f4f6;
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
`;

const SectionErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background-color: #f9fafb;
  border-radius: 12px;
  margin: 20px 0;
`;

const ErrorText = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 12px 0 20px;
`;

const RetryButton = styled.button`
  padding: 8px 20px;
  background-color: ${COLORS.primary};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${COLORS.darkPurple || COLORS.primary};
  }
`;

const ComponentErrorContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #fee2e2;
  border-radius: 6px;
  border: 1px solid #fecaca;
`;

const SmallErrorIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background-color: #ef4444;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: bold;
`;

const SmallErrorText = styled.span`
  font-size: 14px;
  color: #991b1b;
`;

export default ErrorBoundary;